import React, {useEffect, useState} from 'react';
import {
    TextField, FormControl, Select, MenuItem, ListItemText, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper, TableFooter,
} from '@mui/material';
import PSLController from '../../../controllers/PSLController';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import {ezprOrange} from '../../_helpers';
import {NON_PSL_JUSTIFICATIONS} from '../../../utils/constants';
import {BrickContext} from 'olympe';

/**
 * Form that lets the user select between different suppliers, or justify why none of the supplier match its needs
 *
 * @param {Object} props
 * @return {JSX.Element}
 * @constructor
 */
export function SelectProviderSupplierTable(props) {
    const [selectedOption, setSelectedOption] = useState(null);
    const [noneSelected, setNoneSelected] = useState(false);
    const [selectedJustifications, setSelectedJustifications] = useState([]);
    const [comments, setComments] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(2);
    const [renderTable, setRenderTable] = useState(false);
    const [loading, setLoading] = useState(false);

    const queryPSL = () => {
        const skip = page * rowsPerPage;
        PSLController.getPSLRequest(
            props.context,
            props.getPSLRequest,
            props.itemCode.value,
            skip.toString(),
            rowsPerPage.toString(),
            props.companyCode?.value,
        ).then((result) => {
            if (result.response.data.length === 0) {
                props.onSelectNone(true);
            }
            setSuppliers(result.response.data);
            props.setPslSuppliersTotal(result.response.total);
        }).catch((error) => {
            console.error(error);
            props.onSelectNone(true);
            setSuppliers([]);
            props.setPslSuppliersTotal(0);
        }).finally(() => {
            setLoading(false);
        });
        setRenderTable(true);
    };

    useEffect(() => {
        setLoading(true);
        setRenderTable(false);
        setSuppliers([]);
        props.setPslSuppliersTotal(0);
        setNoneSelected(false);
        props.onSelectNone(false);
        setSelectedJustifications([]);
        if (props.itemCode && props.itemCode.value && props.itemCode.value.length > 0) {
            queryPSL();
        } else {
            setLoading(false);
        }
    }, [props.itemCode, props.companyCode, page, rowsPerPage]);

    const handleSelectOption = (option) => () => {
        setSelectedOption(option);
        props.onSupplierSelected(option);
        setNoneSelected(false);
        setSelectedJustifications([]);
        props.onSelectNone(false);
    };

    const handleSelectNone = () => {
        setSelectedOption(null);
        props.onSupplierSelected({});
        setNoneSelected(true);
        props.setJustificationValue([]);
        props.onSelectNone(true);
    };

    const handleJustificationChanges = (event) => {
        const selected = event.target.value;
        setSelectedJustifications(selected);
        const formattedJustifications = selected.length > 0 ? selected.join(', ') : '';
        props.setJustificationValue(formattedJustifications);
    };

    const handleCommentChange = (event) => {
        setComments(event.target.value);
        props.setNonPSLCommentValue(event.target.value);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return <>
        {renderTable && <>
            <TableContainer component={Paper} sx={{marginTop: '2rem', marginBottom: '2rem'}}>
                <Table sx={{minWidth: 650}}>
                    <TableHead sx={{backgroundColor: ezprOrange}}>
                        <TableRow>
                            <TableCell sx={{color: 'white'}}>PSL ID</TableCell>
                            <TableCell sx={{color: 'white'}}>PSL Material ID</TableCell>
                            <TableCell sx={{color: 'white'}}>Supplier code & name</TableCell>
                            <TableCell sx={{color: 'white'}}>Material code & description</TableCell>
                            <TableCell sx={{color: 'white'}}>Status</TableCell>
                            <TableCell sx={{color: 'white'}}>Price unit</TableCell>
                            <TableCell sx={{color: 'white'}}>Lead times</TableCell>
                            <TableCell sx={{color: 'white'}}>Buyer</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {suppliers.length > 0 ? (
                            suppliers.map((supplier, idx) => (
                                <TableRow
                                    key={supplier.pslId}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: selectedOption?.pslId === supplier.pslId ? 'rgba(241, 144, 2, 0.12) !important' : 'rgba(153, 153, 153, 0.04)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(241, 144, 2, 0.18)',
                                        },
                                    }}
                                    selected={selectedOption?.pslId === supplier.pslId}
                                    onClick={handleSelectOption(supplier)}
                                >
                                    <TableCell>{supplier.pslId}</TableCell>
                                    <TableCell>{supplier.materialId}</TableCell>
                                    <TableCell>{`${supplier.childSupplierCode ?? '99'} - ${supplier.childSupplierName ?? 'Undefined'}`}</TableCell>
                                    <TableCell>{`${supplier.materialNumber} - ${supplier.shortDescription}`}</TableCell>
                                    <TableCell>{supplier.statusName}</TableCell>
                                    <TableCell>{supplier.unitPrice}</TableCell>
                                    <TableCell>{supplier.supplierLeadTime}</TableCell>
                                    <TableCell>{supplier.strategicBuyerEmailAddress}</TableCell>
                                </TableRow>
                            ))
                        ) : loading ? (
                            <TableRow>

                                <TableCell colSpan={8} style={{textAlign: 'center'}}>
                                    <CircularProgress/>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} style={{textAlign: 'center'}}>
                                    No data exists
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[2, 5, 10, 25]}
                                colSpan={8}
                                count={props.pslSuppliersTotal}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
            <Button
                fullWidth
                size={'small'}
                className={`popup-dyn-supplier-list-none-button`}
                onClick={handleSelectNone}
            >
                {loading ?
                    <CircularProgress sx={{color: 'white !important'}}/> :
                    <p>{suppliers.length > 0 ? 'Choose another supplier as none of the PSL options meet my needs' : 'No PSL options match your needs. Select a supplier from below'}</p>
                }
            </Button>
        </>}
        {(noneSelected && suppliers.length > 0) && <div className='popup-dyn-justification'>
            <div>Reason why: *</div>
            <FormControl fullWidth>
                <Select
                    labelId="justifications-multiple-checkbox-label"
                    id="justifications-multiple-checkbox"
                    multiple
                    value={selectedJustifications}
                    onChange={handleJustificationChanges}
                    displayEmpty
                    size="small"
                    renderValue={(selected) => {
                        if (selected.length === 0) {
                            return <span style={{color: 'rgba(0, 0, 0, 0.4)'}}>Please select an option</span>;
                        }
                        return selected.join(', ');
                    }}
                >
                    {NON_PSL_JUSTIFICATIONS.map((name) => (
                        <MenuItem key={name} value={name}>
                            <Checkbox checked={selectedJustifications.indexOf(name) > -1} />
                            <ListItemText primary={name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <div>Comments:</div>
            <TextField
                size={'small'}
                value={comments}
                placeholder='Add more comments why you chose a different Supplier than what is suggested to you in the PSL'
                onChange={handleCommentChange}
            />
        </div>}
    </>;
}

SelectProviderSupplierTable.propTypes = {
    itemCode: PropTypes.object,
    getPSLRequest: PropTypes.object,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    onSupplierSelected: PropTypes.func,
    companyCode: PropTypes.object,
    setJustificationValue: PropTypes.func,
    setNonPSLCommentValue: PropTypes.func,
    onSelectNone: PropTypes.func,
    pslSuppliersTotal: PropTypes.number,
    setPslSuppliersTotal: PropTypes.func,
};

